/**
 * QZ Tray Service - Silent Printing Utility with Client-Side Signing
 * 
 * QZ Tray is a free, open-source application that allows web browsers
 * to print directly to any printer without showing a print dialog.
 * 
 * Download: https://qz.io/download/
 * 
 * SILENT PRINTING SETUP (NO POPUP):
 * ==================================
 * To eliminate the "Allow/Block" popup, you need a matching certificate/key pair:
 * 
 * STEP 1: Generate certificate using QZ Tray Site Manager
 *   - Right-click QZ Tray icon in system tray → Advanced → Site Manager
 *   - Click + → Create New
 *   - Click Yes to all prompts (create keys, install, copy to override.crt)
 *   - A folder "QZ Tray Demo Cert" appears on Desktop with:
 *     - digital-certificate.txt (your certificate)
 *     - private-key.pem (your private key)
 * 
 * STEP 2: Configure this service
 *   - Call setQzCertificate() with your certificate and private key
 *   - Or save them to localStorage using saveQzCredentials()
 * 
 * STEP 3: Verify override.crt is installed
 *   - Check C:\Program Files\QZ Tray\override.crt exists
 *   - If not, copy digital-certificate.txt to that location and rename to override.crt
 *   - Restart QZ Tray
 */

import qz from 'qz-tray';

// Connection state
let isConnected = false;
let connectionPromise = null;

// Certificate and private key storage
let currentCertificate = null;
let currentPrivateKey = null;

// LocalStorage keys for persisting credentials
const LS_CERTIFICATE_KEY = 'qz_tray_certificate';
const LS_PRIVATE_KEY_KEY = 'qz_tray_private_key';

/**
 * Load credentials from localStorage on init
 */
const loadStoredCredentials = () => {
  try {
    const storedCert = localStorage.getItem(LS_CERTIFICATE_KEY);
    const storedKey = localStorage.getItem(LS_PRIVATE_KEY_KEY);
    if (storedCert && storedKey) {
      currentCertificate = storedCert;
      currentPrivateKey = storedKey;
      console.log('🔐 QZ Tray credentials loaded from localStorage');
      return true;
    }
  } catch (e) {
    console.warn('Could not load QZ credentials from localStorage:', e);
  }
  return false;
};

// Try to load stored credentials on module init
loadStoredCredentials();

/**
 * Save QZ Tray credentials to localStorage for persistence
 * @param {string} certificate - The certificate content (digital-certificate.txt)
 * @param {string} privateKey - The private key content (private-key.pem)
 */
export const saveQzCredentials = (certificate, privateKey) => {
  try {
    localStorage.setItem(LS_CERTIFICATE_KEY, certificate);
    localStorage.setItem(LS_PRIVATE_KEY_KEY, privateKey);
    currentCertificate = certificate;
    currentPrivateKey = privateKey;
    console.log('✅ QZ Tray credentials saved to localStorage');
    return true;
  } catch (e) {
    console.error('Failed to save QZ credentials:', e);
    return false;
  }
};

/**
 * Clear stored QZ Tray credentials
 */
export const clearQzCredentials = () => {
  try {
    localStorage.removeItem(LS_CERTIFICATE_KEY);
    localStorage.removeItem(LS_PRIVATE_KEY_KEY);
    currentCertificate = null;
    currentPrivateKey = null;
    console.log('🗑️ QZ Tray credentials cleared');
  } catch (e) {
    console.error('Failed to clear QZ credentials:', e);
  }
};

/**
 * Set QZ Tray certificate and private key for signing
 * @param {string} certificate - The certificate content
 * @param {string} privateKey - The private key content
 */
export const setQzCertificate = (certificate, privateKey) => {
  currentCertificate = certificate;
  currentPrivateKey = privateKey;
  console.log('🔐 QZ Tray certificate configured');
};

/**
 * Check if QZ credentials are configured
 */
export const hasQzCredentials = () => {
  return !!(currentCertificate && currentPrivateKey);
};

// Set the signature algorithm to SHA512 (required since QZ Tray 2.1)
qz.security.setSignatureAlgorithm('SHA512');

// Certificate promise - provides the certificate to QZ Tray
qz.security.setCertificatePromise(function(resolve, reject) {
  if (currentCertificate) {
    resolve(currentCertificate);
  } else {
    // No certificate configured - will show popup
    console.warn('⚠️ No QZ certificate configured - popup will appear');
    resolve();
  }
});

// Signature promise - signs each message with the private key
qz.security.setSignaturePromise(function(toSign) {
  return function(resolve, reject) {
    if (!currentPrivateKey) {
      // No private key - can't sign, will show popup
      console.warn('⚠️ No QZ private key configured - popup will appear');
      resolve();
      return;
    }
    
    try {
      // Dynamic import jsrsasign only when needed (reduces bundle size if not used)
      import('jsrsasign').then(({ KEYUTIL, KJUR, stob64, hextorstr }) => {
        try {
          // Parse the private key
          const pk = KEYUTIL.getKey(currentPrivateKey);
          
          // Create signature using SHA512withRSA
          const sig = new KJUR.crypto.Signature({ alg: 'SHA512withRSA' });
          sig.init(pk);
          sig.updateString(toSign);
          const hex = sig.sign();
          
          // Convert hex to base64
          const signature = stob64(hextorstr(hex));
          
          console.log('✅ Message signed successfully');
          resolve(signature);
        } catch (signErr) {
          console.error('❌ Signing failed:', signErr);
          resolve(); // Fallback to popup
        }
      }).catch(importErr => {
        console.error('❌ Failed to load jsrsasign:', importErr);
        resolve(); // Fallback to popup
      });
    } catch (err) {
      console.error('❌ Signing error:', err);
      resolve(); // Fallback to popup
    }
  };
});

/**
 * Check if QZ Tray is installed and running
 */
export const isQzTrayAvailable = async () => {
  try {
    // Check if already connected
    if (qz.websocket.isActive()) {
      isConnected = true;
      return true;
    }
    
    // If there's already a connection attempt in progress, wait for it
    if (connectionPromise) {
      try {
        await connectionPromise;
        return qz.websocket.isActive();
      } catch {
        return false;
      }
    }
    
    // Try to connect with longer timeout (5 seconds)
    const connectPromise = qz.websocket.connect({ retries: 1, delay: 500 });
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Connection timeout')), 5000)
    );
    
    await Promise.race([connectPromise, timeoutPromise]);
    
    // Verify connection is actually active
    if (qz.websocket.isActive()) {
      isConnected = true;
      return true;
    } else {
      isConnected = false;
      return false;
    }
  } catch (error) {
    console.log('QZ Tray not available:', error?.message || error);
    isConnected = false;
    return false;
  }
};

/**
 * Connect to QZ Tray
 */
export const connectToQzTray = async () => {
  try {
    if (qz.websocket.isActive()) {
      isConnected = true;
      return true;
    }

    if (connectionPromise) {
      return connectionPromise;
    }

    connectionPromise = qz.websocket.connect()
      .then(() => {
        isConnected = true;
        console.log('✅ Connected to QZ Tray');
        connectionPromise = null;
        return true;
      })
      .catch((error) => {
        console.error('❌ Failed to connect to QZ Tray:', error?.message || error);
        isConnected = false;
        connectionPromise = null;
        return false;
      });

    return connectionPromise;
  } catch (error) {
    console.error('❌ QZ Tray connection error:', error?.message || error);
    isConnected = false;
    connectionPromise = null;
    return false;
  }
};

/**
 * Disconnect from QZ Tray
 */
export const disconnectFromQzTray = async () => {
  try {
    if (qz.websocket.isActive()) {
      await qz.websocket.disconnect();
    }
    isConnected = false;
  } catch (error) {
    console.error('Error disconnecting from QZ Tray:', error);
  }
};

/**
 * Get list of available printers
 */
export const getQzPrinters = async () => {
  try {
    const connected = await connectToQzTray();
    if (!connected) {
      return [];
    }
    const printers = await qz.printers.find();
    console.log('📋 Available printers:', printers);
    return printers || [];
  } catch (error) {
    console.error('Error getting printers:', error);
    return [];
  }
};

/**
 * Get the default printer
 */
export const getDefaultPrinter = async () => {
  try {
    const connected = await connectToQzTray();
    if (!connected) {
      return null;
    }
    const defaultPrinter = await qz.printers.getDefault();
    console.log('🖨️ Default printer:', defaultPrinter);
    return defaultPrinter;
  } catch (error) {
    console.error('Error getting default printer:', error);
    return null;
  }
};

/**
 * Print raw text/ESC-POS commands to thermal printer
 */
export const printRaw = async (printerName, data) => {
  try {
    const connected = await connectToQzTray();
    if (!connected) {
      throw new Error('QZ Tray not connected');
    }

    const config = qz.configs.create(printerName);
    await qz.print(config, data);
    console.log('✅ Print job sent successfully');
    return true;
  } catch (error) {
    console.error('❌ Print failed:', error);
    throw error;
  }
};

/**
 * Print HTML content
 */
export const printHtml = async (printerName, htmlContent, options = {}) => {
  try {
    const connected = await connectToQzTray();
    if (!connected) {
      throw new Error('QZ Tray not connected');
    }

    const config = qz.configs.create(printerName, {
      margins: options.margins || { top: 0, right: 0, bottom: 0, left: 0 },
      size: options.size || { width: options.width || 80, height: options.height || null },
      units: 'mm',
      ...options
    });

    const data = [{
      type: 'html',
      format: 'plain',
      data: htmlContent
    }];

    await qz.print(config, data);
    console.log('✅ HTML print job sent successfully');
    return true;
  } catch (error) {
    console.error('❌ HTML print failed:', error);
    throw error;
  }
};

/**
 * Print PDF from base64 or URL
 */
export const printPdf = async (printerName, pdfData, options = {}) => {
  try {
    const connected = await connectToQzTray();
    if (!connected) {
      throw new Error('QZ Tray not connected');
    }

    const config = qz.configs.create(printerName, {
      margins: options.margins || { top: 0, right: 0, bottom: 0, left: 0 },
      ...options
    });

    // QZ Tray expects base64 data with format specification
    const data = [{
      type: 'pixel',
      format: 'pdf',
      flavor: 'base64',
      data: pdfData
    }];

    await qz.print(config, data);
    console.log('✅ PDF print job sent successfully');
    return true;
  } catch (error) {
    console.error('❌ PDF print failed:', error);
    throw error;
  }
};

/**
 * Print image from base64 or URL
 */
export const printImage = async (printerName, imageData, options = {}) => {
  try {
    const connected = await connectToQzTray();
    if (!connected) {
      throw new Error('QZ Tray not connected');
    }

    const config = qz.configs.create(printerName, {
      margins: options.margins || { top: 0, right: 0, bottom: 0, left: 0 },
      size: options.size || { width: options.width || 80 },
      units: 'mm',
      ...options
    });

    const data = [{
      type: 'image',
      data: imageData
    }];

    await qz.print(config, data);
    console.log('✅ Image print job sent successfully');
    return true;
  } catch (error) {
    console.error('❌ Image print failed:', error);
    throw error;
  }
};

/**
 * Get QZ Tray connection status
 */
export const getConnectionStatus = () => {
  return {
    isConnected: qz.websocket.isActive(),
    isAvailable: isConnected
  };
};

export default {
  isQzTrayAvailable,
  connectToQzTray,
  disconnectFromQzTray,
  getQzPrinters,
  getDefaultPrinter,
  printRaw,
  printHtml,
  printPdf,
  printImage,
  getConnectionStatus,
  // Certificate management for silent printing
  saveQzCredentials,
  clearQzCredentials,
  setQzCertificate,
  hasQzCredentials
};
